import { FaChartBar, FaExternalLinkAlt } from "react-icons/fa";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import {
  formatDateTime,
  formatNumber,
  formatUnixSecondsDateTime,
  formatUsd,
} from "../formatters";
import ReddicksKpiStrip from "./ReddicksKpiStrip";

const OverviewCard = ({ label, value, meta = null }) => (
  <div className="reddicks-overview-card">
    <span className="reddicks-overview-label">{label}</span>
    <strong className="reddicks-overview-value">{value}</strong>
    {meta ? <span className="reddicks-overview-meta">{meta}</span> : null}
  </div>
);

const LoadingGrid = () => (
  <div className="reddicks-overview-grid">
    {Array.from({ length: 6 }).map((_, index) => (
      <div className="reddicks-overview-card" key={`reddicks-overview-skeleton-${index}`}>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-7 w-32 mt-2" />
        <Skeleton className="h-3 w-24 mt-2" />
      </div>
    ))}
  </div>
);

const ReddicksOverviewSection = ({
  stats,
  overview,
  isLoading,
  hasCachedData,
  error,
  lastUpdated,
  isFresh,
  dexSiteUrl,
}) => {
  const shouldShowLoading = isLoading && !hasCachedData;
  const latestSnapshotMeta = overview?.latestSnapshot?.date
    ? `Snapshot ${formatUnixSecondsDateTime(overview.latestSnapshot.date)}`
    : "Snapshot unavailable";

  return (
    <Card className="leaderboard-surface-card">
      <CardHeader className="pb-4">
        <div className="leaderboard-section-header">
          <div className="leaderboard-section-icon">
            <FaChartBar className="h-5 w-5" />
          </div>
          <div>
            <span className="leaderboard-section-kicker">Reddicks Stats</span>
            <CardTitle className="text-xl">Official RSwap Dashboard</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="leaderboard-data-hint leaderboard-data-hint-market">
          Tx cards use on-ledger counts. USD cards use analytics snapshots.
        </p>
        {shouldShowLoading ? (
          <LoadingGrid />
        ) : (
          <div className="reddicks-overview-grid">
            <OverviewCard
              label="Unified Total Transactions"
              value={formatNumber(stats.grandTotalVolume)}
              meta="All categories combined"
            />
            <OverviewCard
              label="Official RSwap Transactions"
              value={formatNumber(stats.reddicksDexTradeCount)}
              meta="From on-ledger swap scans"
            />
            <OverviewCard
              label="Major Radix DEX Swaps"
              value={formatNumber(stats.swapsTradeCount)}
              meta="Ociswap, CaviarNine, DefiPlaza"
            />
            <OverviewCard
              label="Official Router Volume"
              value={formatUsd(overview?.officialRouter?.totalVolumeUSD, {
                compact: true,
              })}
              meta="Cumulative volume"
            />
            <OverviewCard
              label="Official Router Fees"
              value={formatUsd(overview?.officialRouter?.totalFeesUSD, {
                compact: true,
              })}
              meta="Cumulative fees"
            />
            <OverviewCard
              label="Latest Overall TVL"
              value={formatUsd(overview?.latestSnapshot?.totalValueLockedUSD, {
                compact: true,
              })}
              meta={latestSnapshotMeta}
            />
          </div>
        )}
        <ReddicksKpiStrip
          kpis={overview?.kpis}
          isLoading={isLoading}
          hasCachedData={hasCachedData}
        />

        <div className="reddicks-overview-footer">
          <div className="reddicks-overview-meta-row">
            <span>Last Updated: {formatDateTime(lastUpdated)}</span>
            <a
              href={dexSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="reddicks-dex-link"
            >
              Open RSwap
              <FaExternalLinkAlt className="h-3 w-3" />
            </a>
          </div>

          {error ? (
            <p className="text-sm text-amber-300">
              Reddicks analytics unavailable. Showing what we can from cached or on-ledger data.
            </p>
          ) : null}
          {!isFresh && hasCachedData ? (
            <p className="text-sm text-amber-200">
              Showing cached RSwap analytics snapshot while live refresh catches up.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default ReddicksOverviewSection;
